/**
 * Phase 13 Step 5 — Summary card for a geometry_sweep detail page.
 *
 * Shows:
 *   * Overall Pk + 95 % CI (from `result.overall_pk` + bounds).
 *   * n_points run, sampler kind, seed.
 *   * Wall-time (completed_at − started_at).
 *
 * Purely presentational. Tolerates a partially-populated campaign
 * (e.g. queued / running / failed) — each field falls back to "—"
 * when missing.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target } from "lucide-react";

function fmtPk(v) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return Number(v).toFixed(3);
}
function fmtCi(lo, hi) {
    if (lo == null || hi == null) return "—";
    return `[${Number(lo).toFixed(3)}, ${Number(hi).toFixed(3)}]`;
}
function fmtWall(startedAt, completedAt) {
    if (!startedAt || !completedAt) return "—";
    try {
        const ms = new Date(completedAt) - new Date(startedAt);
        if (!Number.isFinite(ms) || ms < 0) return "—";
        const s = Math.round(ms / 1000);
        if (s < 60) return `${s} s`;
        const m = Math.floor(s / 60);
        const r = s % 60;
        return `${m}m ${r}s`;
    } catch {
        return "—";
    }
}

export default function SummaryCard({ campaign }) {
    const result = campaign?.result || null;
    return (
        <Card data-testid="geom-summary-card">
            <CardHeader className="flex flex-row items-center gap-2">
                <Target className="size-4 text-muted-foreground" />
                <CardTitle className="text-base">Campaign summary</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Overall Pk</div>
                        <div
                            className="text-xl font-semibold tabular-nums"
                            data-testid="geom-summary-pk"
                        >
                            {fmtPk(result?.overall_pk)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">95 % CI</div>
                        <div
                            className="text-base text-muted-foreground tabular-nums"
                            data-testid="geom-summary-ci"
                        >
                            {fmtCi(result?.overall_ci_lower, result?.overall_ci_upper)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Points</div>
                        <div
                            className="text-base tabular-nums"
                            data-testid="geom-summary-n-points"
                        >
                            {campaign?.n_points ?? "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Sampler</div>
                        <div
                            className="text-base capitalize"
                            data-testid="geom-summary-sampler"
                        >
                            {campaign?.sampler?.kind || "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Seed</div>
                        <div
                            className="text-base tabular-nums"
                            data-testid="geom-summary-seed"
                        >
                            {campaign?.seed ?? "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Wall time</div>
                        <div
                            className="text-base tabular-nums"
                            data-testid="geom-summary-wall"
                        >
                            {fmtWall(campaign?.started_at, campaign?.completed_at)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Best point</div>
                        <div
                            className="text-xs text-muted-foreground tabular-nums"
                            data-testid="geom-summary-best"
                        >
                            {result?.best_point
                                ? `r=${Math.round(result.best_point.range_m)}m, b=${Math.round(result.best_point.bearing_deg)}°`
                                : "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Worst point</div>
                        <div
                            className="text-xs text-muted-foreground tabular-nums"
                            data-testid="geom-summary-worst"
                        >
                            {result?.worst_point
                                ? `r=${Math.round(result.worst_point.range_m)}m, b=${Math.round(result.worst_point.bearing_deg)}°`
                                : "—"}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
